import React, { FunctionComponent } from 'react';

import packageStaticProps from '@/utility/nextJs/packageStaticProps';
import ImageHeaderLayout from '@/pages/layouts/ImageHeaderLayout';

import LandingPageService, { ILandingPageProps } from '@/pageGeneration/services/LandingPageService';

import LandingPageContentSection from '@/components/landingPages/LandingPageContentSection';
import PageSeo from '@/components/seo/PageSeo';
import InfoSection from '@/components/landingPages/InfoSection';
import UserTestimonialSection from '@/components/landingPages/UserTestimonialSection';
import LandingPageHeaderContent from '@/components/landingPages/LandingPageHeaderContent';
import useCustomPageViews from '@/utility/eventTracking/useCustomPageViews';
import { PAGE_CATEGORIES } from '@/utility/eventTracking/constants';
import UnprotectedPage from '@/components/navigation/UnprotectedPage';

interface IProps extends ILandingPageProps {}

const LandingPageEventPage: FunctionComponent<IProps> = ({
  pageStructure,
  weatherForecast,
  userTestimonialSection,
  landingPageStructure,
  slug,
  seoTitle,
  noIndex = false,
  noFollow = false,
  routes,
  flightSearchFormProps,
  nearbyAirports,
}) => {
  useCustomPageViews({ category: PAGE_CATEGORIES.EVENTS, name: slug });

  if (!pageStructure) return null;

  const HeaderContent = (
    <LandingPageHeaderContent
      structureTitle={landingPageStructure.title}
      pageTitle={pageStructure.title}
      flightSearchFormProps={flightSearchFormProps}
      weatherForecast={weatherForecast}
    />
  );

  return (
    <UnprotectedPage>
      <PageSeo title={`Events: ${seoTitle}`} noIndex={noIndex} noFollow={noFollow} />
      <ImageHeaderLayout imageUrl={pageStructure?.heroImage?.url} headerContent={HeaderContent}>
        <InfoSection
          title={pageStructure.title}
          subtitle={pageStructure.subtitle}
          about={pageStructure.about}
          guide={pageStructure.guide}
          topImageUrl={landingPageStructure.sidebarTop?.url}
          bottomImageUrl={landingPageStructure?.sidebarBottom?.url}
          nearbyAirports={nearbyAirports}
          routes={routes}
        />
        {userTestimonialSection && (
          <LandingPageContentSection>
            <UserTestimonialSection content={userTestimonialSection} />
          </LandingPageContentSection>
        )}
      </ImageHeaderLayout>
    </UnprotectedPage>
  );
};

export default LandingPageEventPage;

export async function getStaticProps({ params }: { params: { slug: string } }): Promise<any> {
  try {
    const { slug } = params;
    const pageProps = await LandingPageService.getEventPageProps(slug);

    if (!pageProps) {
      return {
        notFound: true,
      };
    }
    return packageStaticProps(pageProps);
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}
